import { useEffect, useState } from "react";
import { Link, createFileRoute, useNavigate } from "@tanstack/react-router";
import { ArrowLeft, Loader2, Pencil, Trash2 } from "lucide-react";
import { useTranslation } from "react-i18next";
import { RequireAuth } from "@/components/RequireAuth";
import { ErrorState } from "@/components/ErrorState";
import { AppShell } from "@/components/layout/AppShell";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { TerceroArchiveBanner } from "@/components/terceros/TerceroArchiveBanner";
import { TerceroUnarchiveButton } from "@/components/terceros/TerceroUnarchiveButton";
import {
  canManageTerceroLifecycle,
  terceroLifecycleButtons,
} from "@/components/terceros/tercero-lifecycle";
import { useActiveTeam } from "@/lib/team-context";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import { TerceroForm } from "@/components/terceros/TerceroForm";
import { TerceroDeleteDialog } from "@/components/terceros/TerceroDeleteDialog";
import { DocumentAttachments } from "@/components/documents/DocumentAttachments";
import { useTercero } from "@/lib/api/terceros.queries";
import { useActiveCompany } from "@/lib/company-context";
import {
  getDepartmentName,
  getMunicipalityName,
} from "@facturar/locations";
import type { TerceroDetail } from "@/lib/api/terceros";

export const Route = createFileRoute(
  "/companies/$companyId/terceros/$terceroId",
)({
  component: TerceroDetailPage,
});

function TerceroDetailPage() {
  return (
    <RequireAuth>
      <AppShell>
        <TerceroDetailContent />
      </AppShell>
    </RequireAuth>
  );
}

function TerceroDetailContent() {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { companyId, terceroId } = Route.useParams();
  const { setActiveCompanyId } = useActiveCompany();
  const { activeTeam } = useActiveTeam();
  const terceroQuery = useTercero(companyId, terceroId);
  const [editing, setEditing] = useState(false);
  const [deleteOpen, setDeleteOpen] = useState(false);
  const canWrite = canManageTerceroLifecycle(activeTeam?.role);

  useEffect(() => {
    if (companyId) setActiveCompanyId(companyId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [companyId]);

  if (terceroQuery.isPending) {
    return (
      <div className="flex items-center justify-center py-16">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );
  }

  if (terceroQuery.isError || !terceroQuery.data) {
    return (
      <div className="mx-auto max-w-3xl pt-6">
        <ErrorState
          title={t("terceros.detail.error.title")}
          description={t("terceros.detail.error.body")}
          onRetry={() => void terceroQuery.refetch()}
          retryLabel={t("common.retry")}
        />
      </div>
    );
  }

  const tercero = terceroQuery.data;
  const lifecycle = terceroLifecycleButtons({
    mode: "edit",
    status: tercero.status,
    canWrite,
  });

  return (
    <div className="mx-auto flex max-w-4xl flex-col gap-6">
      <div className="flex flex-col gap-3">
        <Link
          to="/companies/$companyId/terceros"
          params={{ companyId }}
          className="inline-flex w-fit items-center gap-1 text-xs text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft className="h-3 w-3" />
          {t("terceros.detail.back")}
        </Link>
        <div className="flex flex-wrap items-start justify-between gap-4">
          <div className="min-w-0">
            <div className="flex flex-wrap items-center gap-2">
              <h1 className="text-2xl font-semibold tracking-tight">
                {tercero.legalName}
              </h1>
              <Badge className="text-[10px] uppercase">
                {t(`terceros.kind.${tercero.kind}`)}
              </Badge>
              {tercero.status === "archived" ? (
                <Badge variant="muted" className="text-[10px] uppercase">
                  {t("terceros.status.archived")}
                </Badge>
              ) : null}
            </div>
            <p className="mt-1 font-mono text-xs text-muted-foreground">
              {tercero.identificationType} {tercero.identificationNumber}
              {tercero.dv ? `-${tercero.dv}` : null}
            </p>
            {tercero.tradeName ? (
              <p className="mt-0.5 text-xs text-muted-foreground">
                {tercero.tradeName}
              </p>
            ) : null}
          </div>
          <div className="flex flex-wrap gap-2">
            {!editing ? (
              <Button variant="outline" onClick={() => setEditing(true)}>
                <Pencil className="h-4 w-4" />
                {t("terceros.edit")}
              </Button>
            ) : (
              <Button variant="outline" onClick={() => setEditing(false)}>
                {t("common.cancel")}
              </Button>
            )}
            {lifecycle.showUnarchive && !editing ? (
              <TerceroUnarchiveButton
                companyId={companyId}
                terceroId={tercero.id}
              />
            ) : null}
            {lifecycle.showArchive ? (
              <Button
                variant="outline"
                className="text-destructive hover:bg-destructive/10 hover:text-destructive"
                onClick={() => setDeleteOpen(true)}
              >
                <Trash2 className="h-4 w-4" />
                {t("terceros.delete.cta")}
              </Button>
            ) : null}
          </div>
        </div>
      </div>

      {tercero.status === "archived" && !editing ? (
        <TerceroArchiveBanner
          companyId={companyId}
          terceroId={tercero.id}
          canWrite={canWrite}
        />
      ) : null}

      {editing ? (
        <TerceroForm
          mode={{ kind: "edit", companyId, tercero }}
          onUpdated={() => setEditing(false)}
        />
      ) : (
        <OverviewCards
          companyId={companyId}
          terceroId={terceroId}
          tercero={tercero}
        />
      )}

      {/* Soportes vinculados al tercero (RUT, certificación bancaria…) — PF-348 */}
      <DocumentAttachments
        companyId={companyId}
        entityType="tercero"
        entityId={terceroId}
      />

      <TerceroDeleteDialog
        companyId={companyId}
        tercero={tercero}
        open={deleteOpen}
        onOpenChange={setDeleteOpen}
        onArchived={() =>
          void navigate({
            to: "/companies/$companyId/terceros",
            params: { companyId },
          })
        }
      />
    </div>
  );
}

function OverviewCards({
  companyId,
  terceroId,
  tercero,
}: {
  companyId: string;
  terceroId: string;
  tercero: TerceroDetail;
}) {
  const { t } = useTranslation();
  const departmentName = tercero.departmentCode
    ? getDepartmentName(tercero.departmentCode) ?? tercero.departmentCode
    : null;
  const cityName = tercero.cityCode
    ? getMunicipalityName(tercero.cityCode) ?? tercero.cityCode
    : null;
  return (
    <div className="grid gap-4 md:grid-cols-2">
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("terceros.detail.contact.title")}
          </CardTitle>
          <CardDescription>
            {t("terceros.detail.contact.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-3">
            <Field
              label={t("terceros.form.email")}
              value={tercero.email ?? "—"}
            />
            <Field
              label={t("terceros.form.phone")}
              value={tercero.phone ?? "—"}
            />
            <Field
              label={t("terceros.form.department")}
              value={departmentName ?? "—"}
            />
            <Field label={t("terceros.form.city")} value={cityName ?? "—"} />
            <Field
              label={t("terceros.form.address")}
              value={
                [tercero.addressLine1, tercero.addressLine2]
                  .filter(Boolean)
                  .join(", ") || "—"
              }
            />
          </dl>
        </CardContent>
      </Card>
      <Card>
        <CardHeader>
          <CardTitle className="text-base">
            {t("terceros.detail.tax.title")}
          </CardTitle>
          <CardDescription>
            {t("terceros.detail.tax.subtitle")}
          </CardDescription>
        </CardHeader>
        <CardContent>
          <dl className="grid gap-3">
            <Field
              label={t("terceros.form.regime")}
              value={
                tercero.regimeCode
                  ? t(
                      `companies.regime.${tercero.regimeCode}`,
                      tercero.regimeCode,
                    )
                  : "—"
              }
            />
            <Field
              label={t("terceros.form.responsibilities")}
              value={
                tercero.responsibilities.length > 0
                  ? tercero.responsibilities.join(", ")
                  : "—"
              }
            />
            <Field
              label={t("terceros.form.isLargeTaxpayer")}
              value={
                tercero.isLargeTaxpayer ? t("common.yes") : t("common.no")
              }
            />
            <Field
              label={t("terceros.form.isSelfWithholder")}
              value={
                tercero.isSelfWithholder ? t("common.yes") : t("common.no")
              }
            />
            {tercero.notes ? (
              <Field
                label={t("terceros.form.notes")}
                value={tercero.notes}
              />
            ) : null}
          </dl>
        </CardContent>
      </Card>
      {tercero.kind !== "employee" ? (
        <Card className="md:col-span-2">
          <CardHeader>
            <CardTitle className="text-base">
              {t("terceros.detail.activity.title")}
            </CardTitle>
            <CardDescription>
              {t("terceros.detail.activity.subtitle")}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex flex-wrap gap-2">
            {tercero.kind === "customer" || tercero.kind === "both" ? (
              <Button asChild variant="outline" size="sm">
                <Link
                  to="/companies/$companyId/cartera/$terceroId"
                  params={{ companyId, terceroId }}
                  search={{ side: "receivables" }}
                >
                  {t("terceros.detail.activity.receivables")}
                </Link>
              </Button>
            ) : null}
            {tercero.kind === "supplier" || tercero.kind === "both" ? (
              <Button asChild variant="outline" size="sm">
                <Link
                  to="/companies/$companyId/cartera/$terceroId"
                  params={{ companyId, terceroId }}
                  search={{ side: "payables" }}
                >
                  {t("terceros.detail.activity.payables")}
                </Link>
              </Button>
            ) : null}
          </CardContent>
        </Card>
      ) : null}
    </div>
  );
}

function Field({ label, value }: { label: string; value: string }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase text-muted-foreground">
        {label}
      </dt>
      <dd className="mt-0.5 text-sm">{value}</dd>
    </div>
  );
}
